import { createExportApi, openExportDialog, type ExportRuntime } from '@overlayqa/shared/ui';
import { FUNNEL_EVENTS, funnelEventProperties, type ExportProvider, type ExportResult } from '@overlayqa/shared';
import '@overlayqa/shared/ui/styles.css';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ISSUE_MUTATION_INVALIDATE_KEYS } from '../hooks/issueMutationInvalidation';
import { SelectDropdown } from '../components/shared/SelectDropdown';
import { fetchApi } from './api/client';
import { ticketsApi } from './api/tickets';
import { log } from './logger';

/**
 * The dashboard's ONE export path: the shared destination chooser the
 * extension renders (ExportDialog), for every provider.
 *
 * Both the Issues table's bulk action and the issue page's Issue actions open
 * it. Until 2026-10-22 only ClickUp, GitHub and Basecamp did (this file was
 * clickupExport.tsx); Jira, Linear, Asana, Notion and Trello were resolved to
 * the FIRST project / team / workspace / board the account could see and
 * exported there with no picker, so a task landed where the user never chose
 * and that project's members could read it (board P1, 2026-10-22). The
 * chooser's execution (useExportExecution.ts) chunks the request, reads the
 * per-row outcome, invalidates the issue queries, and emits `issue_exported`
 * plus the activation funnel's export step with the same props the deleted
 * useBulkActions emitters sent, so the dashboard's funnel metric is unchanged.
 */
export function useExportDialog() {
  const queryClient = useQueryClient();
  return useCallback(async (provider: ExportProvider, token: string | undefined, ids: string[]): Promise<ExportResult[] | null> => {
    const { capture } = await import('./analytics');
    if (!token) throw new Error('Your session expired. Refresh the page and try again.');
    const issues = await Promise.all(ids.map(id => ticketsApi.get(token, id)));
    const runtime: ExportRuntime = {
      api: createExportApi((path, init) => fetchApi(path, { token, method: init?.method, body: init?.body as string | undefined })),
      debug: { log: (...args) => log.info(`[${provider} export]`, { args }), warn: (...args) => log.warn(`[${provider} export]`, { args }), error: (...args) => log.warn(`[${provider} export]`, { args }) },
      track: capture,
      trackExportUsed: properties => capture(FUNNEL_EVENTS.exportUsed, funnelEventProperties('dashboard', properties)),
    };
    return openExportDialog({ provider, issues, runtime, onExportComplete: () => {
      for (const queryKey of ISSUE_MUTATION_INVALIDATE_KEYS) void queryClient.invalidateQueries({ queryKey });
    }, SelectDropdown: props => <SelectDropdown id={props.id} value={props.value} onChange={props.onChange} options={[...props.options]} disabled={props.disabled} className={props.className} aria-label={props['aria-label']} /> });
  }, [queryClient]);
}
